'use client';

/**
 * The route an authentication interruption left behind, held across the round
 * trip through the sign-in provider.
 *
 * Ratified flow (`planning/auth-session-expiry/plan.md` D1): a dead session
 * sends the user to the home page with the auth modal open — never a sign-in
 * prompt over an unauthorized session screen — and the page they were on is
 * restored once they authenticate. The gallery's parked team action rides the
 * same carrier: a logged-out Copy or Deploy parks its landing route here so the
 * interrupted team is on screen again after sign-in, which is what the resume
 * needs — an intent with no team on screen has nothing to resume into.
 *
 * `sessionStorage`, not `localStorage`: the entry describes THIS tab's
 * interruption, must survive the hard navigation home, and must die with the
 * tab rather than teleporting an unrelated future visit.
 */

const AUTH_RETURN_ROUTE_KEY = 'cabal_auth_return_route';

/**
 * Set for the duration of the hop back into a restored route, so the session
 * lane can tell "this 403/404 is the tail of a re-auth" from an ordinary one.
 * D1 requires a route that is no longer reachable to land the user home, not on
 * `/forbidden` or the not-found page.
 */
const AUTH_RETURN_RESTORE_AT_KEY = 'cabal_auth_return_restore_at';

/**
 * The marker only speaks for the navigation it was set for. Without a bound, an
 * unconsumed marker would sit in storage and reroute an unrelated 403 an hour
 * later.
 */
const AUTH_RETURN_RESTORE_TTL_MS = 60_000;

export const HOME_ROUTE = '/';

/** Reject anything that is not a same-origin absolute path (`//host` included). */
function isRestorableRoute(route: string): boolean {
  return (
    route.startsWith(HOME_ROUTE) &&
    !route.startsWith('//') &&
    route !== HOME_ROUTE
  );
}

function readSessionStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). The resume is
    // a convenience — never let it break the sign-out path.
    return null;
  }
}

/**
 * Remember the route to come back to, immediately before handing off to sign-in
 * (or to the redirect home). Called only from a path that is about to interrupt
 * the user, so an ordinary visit to `/` never gets an entry.
 *
 * `route` names the destination explicitly for callers whose interruption is an
 * in-page action rather than a navigation — a logged-out team action is on the
 * landing already and must come back to THAT team, not to whatever the address
 * bar happened to carry. Omitted, the current location is the destination.
 */
export function captureAuthReturnRoute(input: { route?: string } = {}): void {
  if (typeof window === 'undefined') return;
  const route =
    input.route ?? `${window.location.pathname}${window.location.search}`;
  if (!isRestorableRoute(route)) return;
  try {
    window.sessionStorage.setItem(AUTH_RETURN_ROUTE_KEY, route);
  } catch {
    // See readSessionStorage.
  }
}

/** Is there an interrupted route waiting? Does not consume it. */
export function peekAuthReturnRoute(): string | null {
  if (typeof window === 'undefined') return null;
  const route = readSessionStorage(AUTH_RETURN_ROUTE_KEY);
  if (!route || !isRestorableRoute(route)) return null;
  return route;
}

export function clearAuthReturnRoute(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(AUTH_RETURN_ROUTE_KEY);
  } catch {
    // See readSessionStorage.
  }
}

/**
 * Read and clear in one step. Consume-once: the entry is spent the moment the
 * redirect is issued, so a later manual visit to `/` stays on `/`.
 */
export function consumeAuthReturnRoute(): string | null {
  const route = peekAuthReturnRoute();
  clearAuthReturnRoute();
  return route;
}

const AUTH_RETURN_RESTORE_SEPARATOR = '|';

function toPathname(route: string): string {
  const queryIndex = route.indexOf('?');
  return queryIndex === -1 ? route : route.slice(0, queryIndex);
}

/**
 * Arm the restore marker immediately before navigating into the stored route.
 *
 * The marker records WHICH route it speaks for, not just when it was armed: a
 * bare timestamp would be consumed by whatever session surface happened to
 * 403/404 next, so a successful restore followed by an unrelated forbidden
 * session in the same minute would silently reroute the wrong navigation home.
 *
 * The whole route is kept, query included. Matching is still by pathname, but a
 * landing restore is a SOFT navigation onto a page that is already mounted: the
 * query it carries is the only announcement the landing gets, and reading it
 * off the address bar would mean guessing when the router got round to writing
 * it.
 */
export function markAuthReturnRestore(route: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      AUTH_RETURN_RESTORE_AT_KEY,
      `${Date.now()}${AUTH_RETURN_RESTORE_SEPARATOR}${route}`,
    );
  } catch {
    // See readSessionStorage.
  }
}

export function clearAuthReturnRestore(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(AUTH_RETURN_RESTORE_AT_KEY);
  } catch {
    // See readSessionStorage.
  }
}

/**
 * The route being restored right now, read exactly once and only while the
 * browser is on the page it was armed for. Consume-once, route-scoped and
 * TTL-bounded, so an ordinary 403 elsewhere in the same tab is never mistaken
 * for a restore.
 */
export function consumeAuthReturnRestoreRoute(): string | null {
  if (typeof window === 'undefined') return null;

  const marker = readSessionStorage(AUTH_RETURN_RESTORE_AT_KEY);
  if (!marker) return null;

  const separatorIndex = marker.indexOf(AUTH_RETURN_RESTORE_SEPARATOR);
  if (separatorIndex === -1) {
    clearAuthReturnRestore();
    return null;
  }

  const markedAtMs = Number(marker.slice(0, separatorIndex));
  const markedRoute = marker.slice(separatorIndex + 1);
  if (toPathname(markedRoute) !== window.location.pathname) {
    // A different route is failing — leave the marker for the restore it was
    // armed for, which may not have resolved yet.
    return null;
  }

  clearAuthReturnRestore();
  if (!Number.isFinite(markedAtMs)) return null;
  return Date.now() - markedAtMs <= AUTH_RETURN_RESTORE_TTL_MS
    ? markedRoute
    : null;
}

/**
 * True exactly once for the route the marker was armed for — the same read as
 * {@link consumeAuthReturnRestoreRoute}, for callers that only need to know
 * whether this navigation is the tail of a re-auth.
 */
export function consumeAuthReturnRestore(): boolean {
  return consumeAuthReturnRestoreRoute() != null;
}

/**
 * Where to send a user whose session route turned out to be unreachable. Normal
 * traffic keeps its existing destination (`/forbidden`, not-found); a user who
 * was just restored into a since-deleted or since-revoked route goes home
 * instead of being shown an error for something they did not do.
 */
export function resolveUnreachableRouteDestination(
  fallbackRoute: string,
): string {
  return consumeAuthReturnRestore() ? HOME_ROUTE : fallbackRoute;
}
